import { cn, getValidClassNames } from '@/utils';
import Link from 'next/link';
import { IAdditionalClassNames } from '@netfront/ui-library';
import { EProgressStatus } from '@/models/enums';
import PercentagePill from '../PercentagePill/PercentagePill';
import React from 'react';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import BookmarkPill from '../BookmarkPill/BookmarkPill';
import PresignedUrlImage from '../Image/PresignedUrlImage';

type DiscriminatedType<K extends string, T> = T & { type: K };
type BaseModuleCard = {
  href: string;
  status: EProgressStatus;
  title: string;
  iconUrl: string;
  imageId: string | number;
};
type PartialIfLoading<T> =
  | ({
      isLoading: true;
    } & Partial<T>)
  | ({
      isLoading: false;
    } & T);
type DashboardCardData = BaseModuleCard & { percentageComplete: number };

type ExerciseToolboxCardData = BaseModuleCard & { isBookmarked?: boolean; onBookmarkToggle?: () => Promise<unknown> };
type DashboardCard = DiscriminatedType<'DashboardCard', PartialIfLoading<DashboardCardData>>;
type QuickLinkCard = DiscriminatedType<'QuickLinkCard', PartialIfLoading<DashboardCardData>>;
type ExerciseToolboxCard = DiscriminatedType<'ExerciseToolbox', PartialIfLoading<ExerciseToolboxCardData>>;
type ModuleCard = DashboardCard | ExerciseToolboxCard | QuickLinkCard;
type ModuleCardProps = ModuleCard & IAdditionalClassNames;

export const ModuleCard = ({ additionalClassNames, ...props }: ModuleCardProps) => {
  const skeletonOr = (component: (cardDetails: BaseModuleCard) => React.ReactNode, skeletonClassName: string) => {
    if (props.isLoading) {
      return <SkeletonLoader additionalClassNames={skeletonClassName} />;
    }

    return component(props);
  };

  const dashboardCardSkeletonOr = (component: (cardDetails: DashboardCardData) => React.ReactNode, skeletonClassName: string) => {
    if (props.type !== 'DashboardCard') {
      return null;
    }

    if (props.isLoading) {
      return <SkeletonLoader additionalClassNames={skeletonClassName} />;
    }

    return component(props);
  };

  const quickLinkCardSkeletonOr = (component: (cardDetails: DashboardCardData) => React.ReactNode, skeletonClassName: string) => {
    if (props.type !== 'QuickLinkCard') {
      return null;
    }

    if (props.isLoading) {
      return <SkeletonLoader additionalClassNames={skeletonClassName} />;
    }

    return component(props);
  };

  const exerciseToolboxCardSkeletonOr = (
    component: (cardDetails: ExerciseToolboxCardData) => React.ReactNode,
    skeletonClassName: string,
  ) => {
    if (props.type !== 'ExerciseToolbox') {
      return null;
    }

    if (props.isLoading) {
      return <SkeletonLoader additionalClassNames={skeletonClassName} />;
    }

    return component(props);
  };

  const isInactive = props.isLoading || props.status === EProgressStatus.Locked || props.status === EProgressStatus.ComingSoon;
  const cardClassName = cn(
    'bg-secondary text-secondary-foreground flex flex-col flex-1 h-full rounded shadow-lg overflow-hidden p-2 px-3 no-underline',
    isInactive && 'cursor-not-allowed',
    props.status === EProgressStatus.ComingSoon && 'opacity-60',
    additionalClassNames,
  );

  const cardContent = (
    <>
      {exerciseToolboxCardSkeletonOr(
        ({ isBookmarked, onBookmarkToggle, status }) =>
          status !== EProgressStatus.ComingSoon && isBookmarked !== undefined && onBookmarkToggle ? (
            <BookmarkPill key={props.href} isBookmarked={isBookmarked} onBookmarkToggle={onBookmarkToggle} className="ml-auto pointer" />
          ) : null,
        'w-12 h-[1lh] my-2 ml-auto',
      )}

      {dashboardCardSkeletonOr(
        ({ percentageComplete }) => (
          <PercentagePill percentage={percentageComplete} className="ml-auto mb-2" />
        ),
        'w-12 h-[1lh] my-2 ml-auto',
      )}

      {quickLinkCardSkeletonOr(
        ({ percentageComplete }) => (
          <PercentagePill percentage={percentageComplete} className="ml-auto mb-2" />
        ),
        'w-12 h-[1lh] my-2 ml-auto',
      )}

      <div className="bg-white p-4 rounded shadow mb-4 flex-1">
        {skeletonOr(
          ({ iconUrl, title, imageId, status }) => (
            <div className="h-20 w-20 mx-auto mb-6 flex items-center justify-center">
              {status === EProgressStatus.ComingSoon ? (
                <img src="/images/coming-soon-icon.svg" alt="Coming soon" height={80} width={80} className="object-contain" />
              ) : (
                <PresignedUrlImage
                  imageId={imageId}
                  src={iconUrl}
                  height={80}
                  width={80}
                  alt={`${title} icon`}
                  aria-hidden="true"
                  className="object-contain"
                />
              )}
            </div>
          ),
          'h-20 w-20 mx-auto mb-6',
        )}

        {skeletonOr(
          ({ title, status }) => (
            <p
              className={getValidClassNames({
                'text-md weight-700 text-center mb-0': true,
                'text-primary': status === EProgressStatus.Completed,
              })}
            >
              {title}
            </p>
          ),
          'h-[1lh] w-full',
        )}
      </div>

      {skeletonOr(
        ({ status }) => (
          <div className="flex items-center gap-2 py-2 weight-700">
            {(status === EProgressStatus.InProgress || status === EProgressStatus.NotStarted) && (
              <span className="text-lg">{status === EProgressStatus.NotStarted ? 'Begin' : 'Continue'}</span>
            )}

            {status === EProgressStatus.Completed && <span className="text-lg">Review</span>}

            {status === EProgressStatus.Locked && <span className="color-black text-lg">Locked</span>}

            {status === EProgressStatus.ComingSoon && <span className="color-black text-lg">Coming soon</span>}

            <img className="ml-auto" src="/images/arrow-right.svg" alt="Arrow" />
          </div>
        ),
        'h-6 w-full my-2',
      )}
    </>
  );

  return isInactive ? (
    <div className={cardClassName}>{cardContent}</div>
  ) : (
    <Link className={cardClassName} href={props.href}>{cardContent}</Link>
  );
};
